import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { ArrowLeft, Loader2, Save, BellRing } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ANY_VALUE = '__any__';

const AlertFormPage = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;

  // Grandfathered users who can still use "Any grade"
  const GRANDFATHERED_EMAILS = ['ismail.medfac@gmail.com', 'thepacifier1993@gmail.com'];
  const canUseAnyGrade = user && GRANDFATHERED_EMAILS.includes(user.email);

  const [occupations, setOccupations] = useState([]);
  const [grades, setGrades] = useState([]);
  const [regions, setRegions] = useState([]);
  const [locations, setLocations] = useState([]);

  const [name, setName] = useState('');
  const [occupation, setOccupation] = useState(ANY_VALUE);
  const [grade, setGrade] = useState('');
  const [region, setRegion] = useState(ANY_VALUE);
  const [location, setLocation] = useState(ANY_VALUE);
  const [keywords, setKeywords] = useState('');
  const [active, setActive] = useState(true);

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const optionsRes = await axios.get(`${API_URL}/api/filters/options`);
        setOccupations(optionsRes.data.occupations || []);
        setGrades(optionsRes.data.grades || []);
        setRegions(optionsRes.data.regions || []);
        setLocations(optionsRes.data.locations || []);

        if (isEdit) {
          const alertRes = await axios.get(`${API_URL}/api/alerts/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setName(alertRes.data.name || '');
          setOccupation(alertRes.data.occupation || ANY_VALUE);
          setGrade(alertRes.data.grade || ANY_VALUE);
          setRegion(alertRes.data.region || ANY_VALUE);
          setLocation(alertRes.data.location || ANY_VALUE);
          setKeywords(alertRes.data.keywords || '');
          setActive(alertRes.data.active);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
        toast.error('Failed to load data');
      } finally {
        setFetching(false);
      }
    };
    fetchData();
  }, [id, isEdit, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Please enter an alert name');
      return;
    }

    // Non-grandfathered users must select a grade
    if (!canUseAnyGrade && (!grade || grade === ANY_VALUE)) {
      toast.error('Please select a grade for your alert');
      return;
    }

    // Grade is required
    if (!grade || grade === ANY_VALUE) {
      toast.error('Please select a grade');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: name,
        occupation: occupation === ANY_VALUE ? null : occupation,
        grade: grade,
        region: "UK",  // Always UK
        location: "UK",  // Always UK
        keywords: keywords || null,
        active: active,
      };

      if (isEdit) {
        await axios.put(`${API_URL}/api/alerts/${id}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Alert updated successfully');
      } else {
        await axios.post(`${API_URL}/api/alerts`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success('Alert created! Matching jobs are being found.');
      }
      navigate('/alerts');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save alert');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-[#00A651]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl" data-testid="alert-form-page">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/alerts')}
            className="flex items-center gap-2 text-slate-600 hover:text-[#00A651] mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Alerts
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#E2F5EA] flex items-center justify-center">
              <BellRing className="w-6 h-6 text-[#00A651]" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {isEdit ? 'Edit Alert' : 'Create New Alert'}
              </h1>
              <p className="text-slate-600">
                {isEdit ? 'Update your alert criteria' : 'Set up filters to match healthcare jobs'}
              </p>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-6">
            {/* Alert Name */}
            <div>
              <Label htmlFor="name" className="text-slate-700 mb-2 block">
                Alert Name *
              </Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Consultant Cardiology Jobs in London"
                required
                className="h-12"
                data-testid="alert-name-input"
              />
              <p className="text-xs text-slate-500 mt-1">
                Give your alert a descriptive name to easily identify it
              </p>
            </div>

            {/* Occupation */}
            <div>
              <Label className="text-slate-700 mb-2 block">Occupation / Specialty</Label>
              <Select value={occupation} onValueChange={setOccupation}>
                <SelectTrigger className="h-12" data-testid="occupation-select">
                  <SelectValue placeholder="Select occupation" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_VALUE}>Any occupation</SelectItem>
                  {occupations.map((occ) => (
                    <SelectItem key={occ} value={occ}>{occ}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Grade */}
            <div>
              <Label className="text-slate-700 mb-2 block">
                Grade / Band {!canUseAnyGrade && <span className="text-red-500">*</span>}
              </Label>
              <Select value={grade} onValueChange={setGrade}>
                <SelectTrigger className="h-12" data-testid="grade-select">
                  <SelectValue placeholder="Select grade" />
                </SelectTrigger>
                <SelectContent>
                  {canUseAnyGrade && (
                    <SelectItem value={ANY_VALUE}>Any grade</SelectItem>
                  )}
                  {grades.map((g) => (
                    <SelectItem key={g} value={g}>{g}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!canUseAnyGrade && (
                <p className="text-xs text-slate-500 mt-1">
                  Please select a specific grade for your alert
                </p>
              )}
            </div>

            {/* Location - UK only */}
            <div>
              <Label className="text-slate-700 mb-2 block">Location</Label>
              <Select value={region} onValueChange={setRegion}>
                <SelectTrigger className="h-12" data-testid="region-select">
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="UK">UK</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <Label className="text-slate-700">Alert Status</Label>
                <p className="text-sm text-slate-500">
                  {active ? 'Receiving notifications' : 'Notifications paused'}
                </p>
              </div>
              <Switch
                checked={active}
                onCheckedChange={setActive}
                data-testid="active-toggle"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/alerts')}
              className="flex-1 h-12"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 bg-[#00A651] hover:bg-[#008C44] text-white"
              data-testid="save-alert-btn"
            >
              {loading ? (
                <span className="flex items-center">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Saving...
                </span>
              ) : (
                <span className="flex items-center">
                  <Save className="w-5 h-5 mr-2" />
                  {isEdit ? 'Update Alert' : 'Create Alert'}
                </span>
              )}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default AlertFormPage;
